/**
 * Umami Analytics Hook
 * 
 * Privacy-vriendelijke analytics tracking zonder cookies.
 * Gebruik deze hook om custom events te tracken.
 * 
 * @example
 * const { trackEvent, trackPageView } = useUmami();
 * trackEvent('signup_completed', { userType: 'candidate' });
 */

// Umami tracking functions
export function useUmami() {
  /**
   * Track een custom event
   * @param {string} eventName - Naam van het event (bijv. 'signup_started')
   * @param {object} eventData - Extra data (bijv. { userType: 'candidate' })
   */
  const trackEvent = (eventName, eventData = {}) => {
    if (typeof window !== 'undefined' && window.umami) {
      window.umami.track(eventName, eventData);
    } else if (process.env.NODE_ENV === 'development') {
      console.log('[Umami Dev]', eventName, eventData);
    }
  };

  /**
   * Track een pageview (normaal automatisch, maar kan handmatig bij SPA navigatie)
   * @param {string} url - De URL om te tracken (optioneel, default huidige pagina)
   */
  const trackPageView = (url) => {
    if (typeof window !== 'undefined' && window.umami) {
      window.umami.track(props => ({
        ...props,
        url: url || window.location.pathname
      }));
    }
  };

  return {
    trackEvent,
    trackPageView,
    /**
     * Check of Umami geladen is
     */
    isLoaded: typeof window !== 'undefined' && !!window.umami
  };
}

/**
 * Standaard event namen voor consistentie
 */
export const UmamiEvents = {
  // Registratie flow
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  
  // Onboarding
  ONBOARDING_STEP_COMPLETED: 'onboarding_step',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  
  // Matching
  MATCH_VIEWED: 'match_viewed',
  MATCH_ACCEPTED: 'match_accepted',
  MATCH_REJECTED: 'match_rejected',
  
  // Landing page
  CTA_CLICKED: 'cta_clicked',
  MANIFEST_SCROLLED: 'manifest_scrolled',
  
  // General
  CONTACT_CLICKED: 'contact_clicked',
  LEGAL_PAGE_VIEWED: 'legal_page_viewed',
};

/**
 * Helper om user type te tracken
 */
export const trackUserAction = (eventName, userType, additionalData = {}) => {
  if (typeof window !== 'undefined' && window.umami) {
    window.umami.track(eventName, {
      userType,
      ...additionalData
    });
  }
};

export default useUmami;
